import { f as formatErrorMessage, u as useRenderElement, j as jsxRuntimeExports, a as useIsoLayoutEffect, N as NOOP, b as useStableCallback, c as useTimeout, d as cn, e as createLucideIcon, g as constructFrom, n as normalizeDates, h as differenceInCalendarDays, m as millisecondsInHour, t as toDate, i as millisecondsInMinute, k as isSameYear, l as isSameDay, X, o as jj, p as iE, y as yj, q as kv, Q as Qq, r as iZ, s as d71, Z as ZZ, v as dk, w as mK, R as RiRobot2Line, B as Badge, T as Tooltip, x as TooltipTrigger, z as TooltipContent, A as cva, C as RiPriceTag3Line, D as RiShieldCrossLine, E as RiEmotionNormalLine, F as RiEmotionHappyLine, G as RiEmotionSadLine, H as RiTimeFill, I as buttonVariants, J as TooltipProvider, K as useRender } from './Textarea-CSQjctvq.js';
export { L as Button, M as Calendar, O as DatePicker, P as Dialog, S as DialogClose, U as DialogContent, V as DialogDescription, W as DialogFooter, Y as DialogHeader, _ as DialogOverlay, $ as DialogPortal, a0 as DialogTitle, a1 as DialogTrigger, a2 as Input, a3 as Label, a4 as MultiSelect, a5 as OptionsSelect, a6 as Popover, a7 as PopoverContent, a8 as PopoverTrigger, a9 as SIDEBAR_STORAGE_KEY, aa as Select, ab as SelectContent, ac as SelectContentInline, ad as SelectGroup, ae as SelectItem, af as SelectLabel, ag as SelectScrollDownButton, ah as SelectScrollUpButton, ai as SelectSeparator, aj as SelectTrigger, ak as SelectValue, al as Sidebar, am as SidebarContent, an as SidebarFooter, ao as SidebarGroup, ap as SidebarGroupAction, aq as SidebarGroupContent, ar as SidebarGroupLabel, as as SidebarHeader, at as SidebarInput, au as SidebarInset, av as SidebarMenu, aw as SidebarMenuAction, ax as SidebarMenuBadge, ay as SidebarMenuButton, az as SidebarMenuGroup, aA as SidebarMenuItem, aB as SidebarMenuSkeleton, aC as SidebarMenuSub, aD as SidebarMenuSubButton, aE as SidebarMenuSubItem, aF as SidebarProvider, aG as SidebarRail, aH as SidebarSeparator, aI as SidebarSubMenuButton, aJ as SidebarTrigger, aK as Textarea, aL as badgeVariants, aM as iconVariants, aN as selectContentVariants, aO as selectTriggerVariants, aP as useSidebar, aQ as useSidebarOptional } from './Textarea-CSQjctvq.js';
import * as React from 'react';
import { useMemo, useState, useEffect, useRef } from 'react';

const AvatarRootContext = /*#__PURE__*/React.createContext(undefined);
if (process.env.NODE_ENV !== "production") AvatarRootContext.displayName = "AvatarRootContext";
function useAvatarRootContext() {
  const context = React.useContext(AvatarRootContext);
  if (context === undefined) {
    throw new Error(process.env.NODE_ENV !== "production" ? 'Base UI: AvatarRootContext is missing. Avatar parts must be placed within <Avatar.Root>.' : formatErrorMessage(13));
  }
  return context;
}

const avatarStateAttributesMapping = {
  imageLoadingStatus: () => null
};

const AvatarRoot = /*#__PURE__*/React.forwardRef(function AvatarRoot(componentProps, forwardedRef) {
  const {
    className,
    render,
    ...elementProps
  } = componentProps;
  const [imageLoadingStatus, setImageLoadingStatus] = React.useState('idle');
  const state = React.useMemo(() => ({
    imageLoadingStatus
  }), [imageLoadingStatus]);
  const contextValue = React.useMemo(() => ({
    imageLoadingStatus,
    setImageLoadingStatus
  }), [imageLoadingStatus, setImageLoadingStatus]);
  const element = useRenderElement('span', componentProps, {
    state,
    ref: forwardedRef,
    props: elementProps,
    stateAttributesMapping: avatarStateAttributesMapping
  });
  return /*#__PURE__*/jsxRuntimeExports.jsx(AvatarRootContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") AvatarRoot.displayName = "AvatarRoot";

function useImageLoadingStatus(src, {
  referrerPolicy,
  crossOrigin
}) {
  const [loadingStatus, setLoadingStatus] = React.useState('idle');
  useIsoLayoutEffect(() => {
    if (!src) {
      setLoadingStatus('error');
      return NOOP;
    }
    let isMounted = true;
    const image = new window.Image();
    const updateStatus = status => () => {
      if (!isMounted) {
        return;
      }
      setLoadingStatus(status);
    };
    setLoadingStatus('loading');
    image.onload = updateStatus('loaded');
    image.onerror = updateStatus('error');
    if (referrerPolicy) {
      image.referrerPolicy = referrerPolicy;
    }
    image.crossOrigin = crossOrigin ?? null;
    image.src = src;
    return () => {
      isMounted = false;
    };
  }, [src, crossOrigin, referrerPolicy]);
  return loadingStatus;
}

/**
 * The image to be displayed in the avatar.
 * Renders an `<img>` element.
 *
 * Documentation: [Base UI Avatar](https://base-ui.com/react/components/avatar)
 */
const AvatarImage$1 = /*#__PURE__*/React.forwardRef(function AvatarImage(componentProps, forwardedRef) {
  const {
    className,
    render,
    onLoadingStatusChange: onLoadingStatusChangeProp,
    referrerPolicy,
    crossOrigin,
    ...elementProps
  } = componentProps;
  const context = useAvatarRootContext();
  const imageLoadingStatus = useImageLoadingStatus(componentProps.src, {
    referrerPolicy,
    crossOrigin
  });
  const handleLoadingStatusChange = useStableCallback(status => {
    onLoadingStatusChangeProp?.(status);
    context.setImageLoadingStatus(status);
  });
  useIsoLayoutEffect(() => {
    if (imageLoadingStatus !== 'idle') {
      handleLoadingStatusChange(imageLoadingStatus);
    }
  }, [imageLoadingStatus, handleLoadingStatusChange]);
  const state = React.useMemo(() => ({
    imageLoadingStatus
  }), [imageLoadingStatus]);
  const element = useRenderElement('img', componentProps, {
    state,
    ref: forwardedRef,
    props: elementProps,
    stateAttributesMapping: avatarStateAttributesMapping,
    enabled: imageLoadingStatus === 'loaded'
  });
  return element;
});
if (process.env.NODE_ENV !== "production") AvatarImage$1.displayName = "AvatarImage";

/**
 * Rendered when the image fails to load or when no image is provided.
 * Renders a `<span>` element.
 *
 * Documentation: [Base UI Avatar](https://base-ui.com/react/components/avatar)
 */
const AvatarFallback$1 = /*#__PURE__*/React.forwardRef(function AvatarFallback(componentProps, forwardedRef) {
  const {
    className,
    render,
    delay,
    ...elementProps
  } = componentProps;
  const {
    imageLoadingStatus
  } = useAvatarRootContext();
  const [delayPassed, setDelayPassed] = React.useState(delay === undefined);
  const timeout = useTimeout();
  React.useEffect(() => {
    if (delay !== undefined) {
      timeout.start(delay, () => setDelayPassed(true));
    }
    return timeout.clear;
  }, [timeout, delay]);
  const state = React.useMemo(() => ({
    imageLoadingStatus
  }), [imageLoadingStatus]);
  const element = useRenderElement('span', componentProps, {
    state,
    ref: forwardedRef,
    props: elementProps,
    stateAttributesMapping: avatarStateAttributesMapping,
    enabled: imageLoadingStatus !== 'loaded' && delayPassed
  });
  return element;
});
if (process.env.NODE_ENV !== "production") AvatarFallback$1.displayName = "AvatarFallback";

function Avatar({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    AvatarRoot,
    {
      "data-slot": "avatar",
      className: cn(
        "relative flex size-8 shrink-0 overflow-hidden rounded-full",
        className
      ),
      ...props
    }
  );
}
function AvatarImage({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    AvatarImage$1,
    {
      "data-slot": "avatar-image",
      className: cn("aspect-square size-full", className),
      ...props
    }
  );
}
function AvatarFallback({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    AvatarFallback$1,
    {
      "data-slot": "avatar-fallback",
      className: cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className
      ),
      ...props
    }
  );
}

/**
 * @license lucide-react v0.364.0 - ISC
 *
 * This source code is licensed under the ISC license.
 * See the LICENSE file in the root directory of this source tree.
 */


const Banknote = createLucideIcon("Banknote", [
  ["rect", { width: "20", height: "12", x: "2", y: "6", rx: "2", key: "9lu3g6" }],
  ["circle", { cx: "12", cy: "12", r: "2", key: "1c9p78" }],
  ["path", { d: "M6 12h.01M18 12h.01", key: "113zkx" }]
]);

/**
 * @license lucide-react v0.364.0 - ISC
 *
 * This source code is licensed under the ISC license.
 * See the LICENSE file in the root directory of this source tree.
 */


const Clock = createLucideIcon("Clock", [
  ["circle", { cx: "12", cy: "12", r: "10", key: "1mglay" }],
  ["polyline", { points: "12 6 12 12 16 14", key: "68esgv" }]
]);

/**
 * @license lucide-react v0.364.0 - ISC
 *
 * This source code is licensed under the ISC license.
 * See the LICENSE file in the root directory of this source tree.
 */


const Eye = createLucideIcon("Eye", [
  ["path", { d: "M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z", key: "rwhkz3" }],
  ["circle", { cx: "12", cy: "12", r: "3", key: "1v7zrd" }]
]);

/**
 * @license lucide-react v0.364.0 - ISC
 *
 * This source code is licensed under the ISC license.
 * See the LICENSE file in the root directory of this source tree.
 */


const LineChart = createLucideIcon("LineChart", [
  ["path", { d: "M3 3v18h18", key: "1s2lah" }],
  ["path", { d: "m19 9-5 5-4-4-3 3", key: "2osh9i" }]
]);

/**
 * @license lucide-react v0.364.0 - ISC
 *
 * This source code is licensed under the ISC license.
 * See the LICENSE file in the root directory of this source tree.
 */


const Megaphone = createLucideIcon("Megaphone", [
  ["path", { d: "m3 11 18-5v12L3 14v-3z", key: "n962bs" }],
  ["path", { d: "M11.6 16.8a3 3 0 1 1-5.8-1.6", key: "1yl0tm" }]
]);

/**
 * @name constructNow
 * @category Generic Helpers
 * @summary Constructs a new current date using the passed value constructor.
 * @pure false
 *
 * @description
 * The function constructs a new current date using the constructor from
 * the reference date. It helps to build generic functions that accept date
 * extensions and use the current date.
 *
 * It defaults to `Date` if the passed reference date is a number or a string.
 *
 * @param date - The reference date to take constructor from
 *
 * @returns Current date initialized using the given date constructor
 *
 * @example
 * import { constructNow, isSameDay } from 'date-fns'
 *
 * function isToday<DateType extends Date>(
 *   date: DateArg<DateType>,
 * ): boolean {
 *   // If we were to use `new Date()` directly, the function would  behave
 *   // differently in different timezones and return false for the same date.
 *   return isSameDay(date, constructNow(date));
 * }
 */
function constructNow(date) {
  return constructFrom(date, Date.now());
}

/**
 * The {@link differenceInDays} function options.
 */

/**
 * @name differenceInDays
 * @category Day Helpers
 * @summary Get the number of full days between the given dates.
 *
 * @description
 * Get the number of full day periods between two dates. Fractional days are
 * truncated towards zero.
 *
 * One "full day" is the distance between a local time in one day to the same
 * local time on the next or previous day. A full day can sometimes be less than
 * or more than 24 hours if a daylight savings change happens between two dates.
 *
 * To ignore DST and only measure exact 24-hour periods, use this instead:
 * `Math.trunc(differenceInHours(dateLeft, dateRight)/24)|0`.
 *
 * @param laterDate - The later date
 * @param earlierDate - The earlier date
 * @param options - An object with options
 *
 * @returns The number of full days according to the local timezone
 *
 * @example
 * // How many full days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 365
 *
 * @example
 * // How many full days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 0
 *
 * @example
 * // How many full days are between
 * // 1 March 2020 0:00 and 1 June 2020 0:00 ?
 * // Note: because local time is used, the
 * // result will always be 92 days, even in
 * // time zones where DST starts and the
 * // period has only 92*24-1 hours.
 * const result = differenceInDays(
 *   new Date(2020, 5, 1),
 *   new Date(2020, 2, 1)
 * )
 * //=> 92
 */
function differenceInDays(laterDate, earlierDate, options) {
  const [laterDate_, earlierDate_] = normalizeDates(
    options?.in,
    laterDate,
    earlierDate,
  );

  const sign = compareLocalAsc(laterDate_, earlierDate_);
  const difference = Math.abs(
    differenceInCalendarDays(laterDate_, earlierDate_),
  );

  laterDate_.setDate(laterDate_.getDate() - sign * difference);

  // Math.abs(diff in full days - diff in calendar days) === 1 if last calendar day is not full
  // If so, result must be decreased by 1 in absolute value
  const isLastDayNotFull = Number(
    compareLocalAsc(laterDate_, earlierDate_) === -sign,
  );

  const result = sign * (difference - isLastDayNotFull);
  // Prevent negative zero
  return result === 0 ? 0 : result;
}

// Like `compareAsc` but uses local time not UTC, which is needed
// for accurate equality comparisons of UTC timestamps that end up
// having the same representation in local time, e.g. one hour before
// DST ends vs. the instant that DST ends.
function compareLocalAsc(laterDate, earlierDate) {
  const diff =
    laterDate.getFullYear() - earlierDate.getFullYear() ||
    laterDate.getMonth() - earlierDate.getMonth() ||
    laterDate.getDate() - earlierDate.getDate() ||
    laterDate.getHours() - earlierDate.getHours() ||
    laterDate.getMinutes() - earlierDate.getMinutes() ||
    laterDate.getSeconds() - earlierDate.getSeconds() ||
    laterDate.getMilliseconds() - earlierDate.getMilliseconds();

  if (diff < 0) return -1;
  if (diff > 0) return 1;

  // Return 0 if diff is 0; return NaN if diff is NaN
  return diff;
}

function getRoundingMethod(method) {
  return (number) => {
    const round = method ? Math[method] : Math.trunc;
    const result = round(number);
    // Prevent negative zero
    return result === 0 ? 0 : result;
  };
}

/**
 * The {@link differenceInHours} function options.
 */

/**
 * @name differenceInHours
 * @category Hour Helpers
 * @summary Get the number of hours between the given dates.
 *
 * @description
 * Get the number of hours between the given dates.
 *
 * @param laterDate - The later date
 * @param earlierDate - The earlier date
 * @param options - An object with options.
 *
 * @returns The number of hours
 *
 * @example
 * // How many hours are between 2 July 2014 06:50:00 and 2 July 2014 19:00:00?
 * const result = differenceInHours(
 *   new Date(2014, 6, 2, 19, 0),
 *   new Date(2014, 6, 2, 6, 50)
 * )
 * //=> 12
 */
function differenceInHours(laterDate, earlierDate, options) {
  const [laterDate_, earlierDate_] = normalizeDates(
    options?.in,
    laterDate,
    earlierDate,
  );
  const diff = (+laterDate_ - +earlierDate_) / millisecondsInHour;
  return getRoundingMethod(options?.roundingMethod)(diff);
}

/**
 * @name differenceInMilliseconds
 * @category Millisecond Helpers
 * @summary Get the number of milliseconds between the given dates.
 *
 * @description
 * Get the number of milliseconds between the given dates.
 *
 * @param laterDate - The later date
 * @param earlierDate - The earlier date
 *
 * @returns The number of milliseconds
 *
 * @example
 * // How many milliseconds are between
 * // 2 July 2014 12:30:20.600 and 2 July 2014 12:30:21.700?
 * const result = differenceInMilliseconds(
 *   new Date(2014, 6, 2, 12, 30, 21, 700),
 *   new Date(2014, 6, 2, 12, 30, 20, 600)
 * )
 * //=> 1100
 */
function differenceInMilliseconds(laterDate, earlierDate) {
  return +toDate(laterDate) - +toDate(earlierDate);
}

/**
 * The {@link differenceInMinutes} function options.
 */

/**
 * @name differenceInMinutes
 * @category Minute Helpers
 * @summary Get the number of minutes between the given dates.
 *
 * @description
 * Get the signed number of full (rounded towards 0) minutes between the given dates.
 *
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 * @param options - An object with options.
 *
 * @returns The number of minutes
 *
 * @example
 * // How many minutes are between 2 July 2014 12:07:59 and 2 July 2014 12:20:00?
 * const result = differenceInMinutes(
 *   new Date(2014, 6, 2, 12, 20, 0),
 *   new Date(2014, 6, 2, 12, 7, 59)
 * )
 * //=> 12
 *
 * @example
 * // How many minutes are between 10:01:59 and 10:00:00
 * const result = differenceInMinutes(
 *   new Date(2000, 0, 1, 10, 0, 0),
 *   new Date(2000, 0, 1, 10, 1, 59)
 * )
 * //=> -1
 */
function differenceInMinutes(dateLeft, dateRight, options) {
  const diff =
    differenceInMilliseconds(dateLeft, dateRight) / millisecondsInMinute;
  return getRoundingMethod(options?.roundingMethod)(diff);
}

/**
 * The {@link differenceInSeconds} function options.
 */

/**
 * @name differenceInSeconds
 * @category Second Helpers
 * @summary Get the number of seconds between the given dates.
 *
 * @description
 * Get the number of seconds between the given dates.
 *
 * @param laterDate - The later date
 * @param earlierDate - The earlier date
 * @param options - An object with options.
 *
 * @returns The number of seconds
 *
 * @example
 * // How many seconds are between
 * // 2 July 2014 12:30:07.999 and 2 July 2014 12:30:20.000?
 * const result = differenceInSeconds(
 *   new Date(2014, 6, 2, 12, 30, 20, 0),
 *   new Date(2014, 6, 2, 12, 30, 7, 999)
 * )
 * //=> 12
 */
function differenceInSeconds(laterDate, earlierDate, options) {
  const diff = differenceInMilliseconds(laterDate, earlierDate) / 1000;
  return getRoundingMethod(options?.roundingMethod)(diff);
}

/**
 * The {@link isThisYear} function options.
 */

/**
 * @name isThisYear
 * @category Year Helpers
 * @summary Is the given date in the same year as the current date?
 * @pure false
 *
 * @description
 * Is the given date in the same year as the current date?
 *
 * @param date - The date to check
 * @param options - An object with options
 *
 * @returns The date is in this year
 *
 * @example
 * // If today is 25 September 2014, is 2 July 2014 in this year?
 * const result = isThisYear(new Date(2014, 6, 2))
 * //=> true
 */
function isThisYear(date, options) {
  return isSameYear(
    constructFrom(date, date),
    constructNow(date),
  );
}

/**
 * The {@link isToday} function options.
 */

/**
 * @name isToday
 * @category Day Helpers
 * @summary Is the given date today?
 * @pure false
 *
 * @description
 * Is the given date today?
 *
 * @param date - The date to check
 * @param options - An object with options
 *
 * @returns The date is today
 *
 * @example
 * // If today is 6 October 2014, is 6 October 14:00:00 today?
 * const result = isToday(new Date(2014, 9, 6, 14, 0))
 * //=> true
 */
function isToday(date, options) {
  return isSameDay(
    constructFrom(date, date),
    constructNow(date),
  );
}

const ATTACHMENT_ICONS = {
  agent: dk,
  conversation: ZZ,
  customers: d71,
  dashboard: iZ,
  dashboard_widget: iZ,
  onboarding_list: iE,
  regulation_framework: Qq,
  report: kv,
  report_schedule: kv,
  routine_run: yj,
  view: iE,
  work_item: jj
};
function promptInputAttachmentIcon(type) {
  return type && ATTACHMENT_ICONS[type] || mK;
}
function PromptInputDisplayAttachmentChip({
  attachment,
  onRemove,
  className,
  title
}) {
  const AttachmentIcon = promptInputAttachmentIcon(attachment.type);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(
    "div",
    {
      title,
      className: cn(
        "flex h-8 max-w-full items-center gap-1.5 truncate rounded-md border border-border bg-white px-1.5 text-sm font-medium text-foreground",
        className
      ),
      children: [
        attachment.hideIcon ? null : /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "flex size-5 shrink-0 items-center justify-center rounded bg-background", children: /* @__PURE__ */ jsxRuntimeExports.jsx(AttachmentIcon, { className: "size-3 shrink-0 text-muted-foreground" }) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-0 truncate", children: attachment.label }),
        attachment.description ? /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "hidden shrink-0 text-muted-foreground sm:inline", children: attachment.description }) : null,
        onRemove ? /* @__PURE__ */ jsxRuntimeExports.jsx(
          "button",
          {
            type: "button",
            "aria-label": `Remove ${attachment.label}`,
            className: "ml-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-neutral-100 hover:text-foreground",
            onClick: (event) => {
              event.stopPropagation();
              onRemove();
            },
            children: /* @__PURE__ */ jsxRuntimeExports.jsx(X, { className: "size-3" })
          }
        ) : null
      ]
    }
  );
}

const customerImageBgColors = [
  "bg-neutral-200",
  "bg-warning-50",
  "bg-primary-100",
  "bg-danger-50",
  "bg-success-50"
];
const agentImageBgColors = [
  "text-warning-500 bg-warning-100",
  "bg-primary-100 text-primary-500"
];
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}
const userImageVariants = cva(
  "flex shrink-0 items-center justify-center text-xs rounded-full",
  {
    variants: {
      size: {
        xs: "min-w-4 min-h-4 size-4 text-[8px] font-medium",
        sm: "min-w-6 min-h-6 size-6 text-2xs font-medium",
        "sm-plus": "min-w-5.5 min-h-5.5 size-5.5 text-[9px] font-semibold",
        "sm-md": "min-w-5 min-h-5 size-5 text-[8px] font-semibold",
        md: "min-w-8 min-h-8 size-8",
        lg: "min-w-10 min-h-10 size-10 text-sm font-medium"
      },
      type: {
        customer: "bg-primary-50",
        agent: "border-neutral-300"
      }
    },
    defaultVariants: {
      size: "sm",
      type: "customer"
    }
  }
);
function AgentImage(props) {
  if (props.agent == null) {
    return /* @__PURE__ */ jsxRuntimeExports.jsx(UserImage, { size: props.size, children: "UA" });
  }
  const getInitials = (name) => {
    if (!name) return "UC";
    return name.split(" ").map((word) => {
      if (word.match(/^\+[\d]/)) {
        const match = word.match(/^\+[\d]+/);
        return match ? match[0] : "";
      }
      return word.charAt(0).match(/[a-zA-Z]/) ? word.charAt(0).toUpperCase() : "";
    }).join("").slice(0, 2).trim() || "UC";
  };
  const robotIconSize = props.size === "xs" ? "size-2" : props.size === "lg" ? "size-4" : "size-3";
  const label = props.agent.type === "bot" ? /* @__PURE__ */ jsxRuntimeExports.jsx(RiRobot2Line, { className: robotIconSize }) : getInitials(props.agent?.name ?? props.agent?.email ?? "Unknown agent");
  if (props.agent.avatar_url) {
    const avatarSizeMap = {
      xs: "size-4",
      sm: "size-6",
      "sm-plus": "size-5.5",
      "sm-md": "size-5",
      md: "size-8",
      lg: "size-10"
    };
    const avatarSize = avatarSizeMap[props.size ?? "sm"];
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(Avatar, { className: cn(avatarSize, "bg-neutral-200"), children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        AvatarImage,
        {
          src: props.agent.avatar_url,
          alt: props.agent.name || "Agent",
          className: "object-cover object-center"
        }
      ),
      /* @__PURE__ */ jsxRuntimeExports.jsx(AvatarFallback, { className: "bg-neutral-200 text-xs font-medium", children: label })
    ] });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(UserImage, { size: props.size, type: "agent", children: label });
}
function Agents(props) {
  const maxVisible = props.maxVisible ?? 3;
  const size = props.size ?? "sm-md";
  const bordered = props.bordered ?? true;
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex items-center", children: [
    props.employees.slice(0, maxVisible).map((employee, index) => /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { children: /* @__PURE__ */ jsxRuntimeExports.jsx(
        "div",
        {
          className: cn(
            "rounded-full",
            bordered && "border-white border-[1.5px]",
            index !== 0 && "-ml-[8px]"
          ),
          children: /* @__PURE__ */ jsxRuntimeExports.jsx(AgentImage, { size, agent: employee })
        }
      ) }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: getAgentName(employee) })
    ] }, employee.id)),
    props.employees.length > maxVisible && /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
        "div",
        {
          className: cn(
            "text-[8px] bg-neutral-100 rounded-full font-semibold flex items-center justify-center text-black -ml-[8px]",
            bordered && "border-[1.5px] border-white",
            size === "sm" ? "size-6 min-w-6 min-h-6" : size === "sm-plus" ? "size-5.5 min-w-5.5 min-h-5.5" : "size-5 min-w-5 min-h-5"
          ),
          children: [
            "+",
            props.employees.length - maxVisible
          ]
        }
      ) }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-col gap-1", children: props.employees.slice(maxVisible).map((employee) => /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: getAgentName(employee) }, employee.id)) }) })
    ] })
  ] });
}
function AgentImageAndName(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex gap-2 items-center", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(AgentImage, { agent: props.employee, size: "md" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(AgentName, { agent: props.employee })
  ] });
}
function AgentName(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "font-medium", children: getAgentName(props.agent) }),
    props.agent?.type === "bot" && /* @__PURE__ */ jsxRuntimeExports.jsx(Badge, { className: "text-neutral-700", children: "Bot" })
  ] });
}
function getAgentName(agent) {
  const name = (agent?.name ?? "").trim();
  const email = (agent?.email ?? "").trim();
  const phone = (agent?.phone ?? "").trim();
  return name || email || phone || "Unknown agent";
}
function UserImage(props) {
  const bgColor = useMemo(() => {
    const key = typeof props.children === "string" ? props.children : "";
    const hash = hashString(key);
    if (props.type !== "customer") {
      return agentImageBgColors[hash % agentImageBgColors.length];
    }
    return customerImageBgColors[hash % customerImageBgColors.length];
  }, [props.children, props.type]);
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "div",
    {
      className: cn(
        userImageVariants({ ...props }),
        bgColor,
        props.bgType === "gradient-bg" && "bg-[url(/img/gradient-bg.png)] bg-cover"
      ),
      children: props.children
    }
  );
}
function UserPersona(props) {
  const label = (props.name ?? "Unknown Customer")?.split(" ").map((word) => {
    if (word.match(/^\+[\d]/)) {
      const match = word.match(/^\+[\d]+/);
      return match ? match[0] : "";
    }
    return word.charAt(0).match(/[a-zA-Z]/) ? word.charAt(0).toUpperCase() : "";
  }).join("").slice(0, 2).trim() ?? "UC";
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex gap-2 items-center", children: /* @__PURE__ */ jsxRuntimeExports.jsx(UserImage, { size: props.size, type: "customer", children: label }) });
}

function conversationIsSourceDeleted(conversation) {
  return conversation.source_deleted_at != null;
}
function sourceDeletedDisplayName(conversation) {
  const dataSourceKey = conversation.data_source?.key;
  const dataSourceName = conversation.data_source?.name;
  if (dataSourceName) {
    return dataSourceName;
  }
  if (dataSourceKey === "intercom") {
    return "Intercom";
  }
  return "the source system";
}
function shouldShowSourceDeletedBadge(conversation) {
  return conversationIsSourceDeleted(conversation) && conversation.external_url == null;
}
function sourceDeletedBadgeLabel(conversation) {
  const sourceName = sourceDeletedDisplayName(conversation);
  if (sourceName === "the source system") {
    return "Source deleted";
  }
  return `Deleted from ${sourceName}`;
}
function sourceDeletedTooltip(conversation) {
  const sourceName = sourceDeletedDisplayName(conversation);
  return `This conversation may have been deleted from ${sourceName}.`;
}

function DisputeDueDateBadge(props) {
  const { failedDaysLeft = 14, warningDaysLeft = 60 } = props.config ?? {};
  const showIcons = props.showIcons ?? true;
  const daysLeft = differenceInDays(new Date(props.date), /* @__PURE__ */ new Date());
  if (daysLeft === 0) {
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(Badge, { variant: "destructive", className: props.className, children: [
      showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(Clock, { className: "size-3" }),
      "Due today"
    ] });
  }
  if (daysLeft < 0) {
    const absoluteDaysLeft = Math.abs(daysLeft);
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(Badge, { variant: "destructive", className: props.className, children: [
      showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(Clock, { className: "size-3" }),
      "Overdue by ",
      absoluteDaysLeft,
      " day",
      absoluteDaysLeft === 1 ? "" : "s"
    ] });
  }
  if (daysLeft < failedDaysLeft) {
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(Badge, { variant: "destructive", className: props.className, children: [
      showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(Clock, { className: "size-3" }),
      "Due in ",
      daysLeft,
      " day",
      daysLeft === 1 ? "" : "s"
    ] });
  }
  if (daysLeft < warningDaysLeft) {
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(Badge, { variant: "warning", className: props.className, children: [
      showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(Clock, { className: "size-3" }),
      "Due in ",
      daysLeft,
      " day",
      daysLeft === 1 ? "" : "s"
    ] });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Badge, { className: props.className, children: [
    showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(Clock, { className: "size-3" }),
    "Due in ",
    daysLeft,
    " day",
    daysLeft === 1 ? "" : "s"
  ] });
}
function DisputeAcknowledgementDueDateBadge(props) {
  if (props.dispute.acknowledgement_due_at == null) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    DisputeDueDateBadge,
    {
      date: props.dispute.acknowledgement_due_at,
      config: {
        failedDaysLeft: 5,
        warningDaysLeft: 10
      },
      className: props.className
    }
  );
}
function DisputeResolutionDueDateBadge(props) {
  if (props.dispute.resolution_due_at == null) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    DisputeDueDateBadge,
    {
      date: props.dispute.resolution_due_at,
      className: props.className
    }
  );
}

const VISIBLE_BADGE_COUNT = 2;
function ConversationBadges(props) {
  const showIcons = props.showIcons ?? true;
  const slaBreaches = props.conversation.sla_breaches ?? [];
  const sourceDeletedBadge = shouldShowSourceDeletedBadge(
    props.conversation
  ) ? /* @__PURE__ */ jsxRuntimeExports.jsx(
    SourceDeletedBadge,
    {
      conversation: props.conversation
    },
    "source-deleted"
  ) : null;
  const badges = [];
  if (slaBreaches.length > 0) {
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsx(
        SLABreachBadge,
        {
          slaBreaches,
          showIcons
        },
        "sla-breach"
      ),
      label: "SLA Breach"
    });
  }
  if (props.conversation.conversation_customer_sentiment_summary) {
    const sentimentData = getSentimentDisplay(
      props.conversation.conversation_customer_sentiment_summary
    );
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsx(
        SentimentBadge,
        {
          summary: props.conversation.conversation_customer_sentiment_summary,
          showIcons
        },
        "sentiment"
      ),
      label: sentimentData.text
    });
  }
  if (props.conversation.interaction_type === "sales_call") {
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsxs(
        ItemBadge,
        {
          className: "bg-neutral-100 text-black rounded-md",
          children: [
            showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(LineChart, { size: 12 }),
            "Sales call"
          ]
        },
        "sales-call"
      ),
      label: "Sales call"
    });
  }
  if (props.conversation.dispute != null) {
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsx(DisputeBadge, { showIcons }, "dispute"),
      label: "Dispute"
    });
    if (props.conversation.dispute.status === "open") {
      badges.push({
        node: /* @__PURE__ */ jsxRuntimeExports.jsx(
          DisputeDueDateBadge,
          {
            date: props.conversation.dispute.resolution_due_at,
            className: "rounded-full",
            showIcons
          },
          "dispute-due-date"
        ),
        label: "Dispute due date"
      });
    }
  }
  if (props.conversation.customer_complaints?.length > 0) {
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs(ItemBadge, { className: "bg-danger-50 text-danger-500 rounded-md", children: [
          showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(Megaphone, { className: "fill-danger-500", size: 12 }),
          "Complaint"
        ] }) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: props.conversation.customer_complaints[0]?.summary })
      ] }, "complaint"),
      label: "Complaint"
    });
  }
  props.conversation.customer_complaints?.[0]?.categories.forEach(
    (category) => {
      badges.push({
        node: /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "inline-flex min-w-0 max-w-full", children: /* @__PURE__ */ jsxRuntimeExports.jsx(ItemBadge, { className: "min-w-0 max-w-full shrink bg-danger-50 text-danger-500 rounded-md", children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "min-w-0 truncate", children: category.name }) }) }) }),
          /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { className: "max-w-sm whitespace-pre-wrap break-words", children: category.name })
        ] }, `complaint-category-${category.id}`),
        label: category.name
      });
    }
  );
  if (props.conversation.compliance_risk_level === "high") {
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs(ItemBadge, { className: "bg-danger-50 text-danger-500 rounded-md", children: [
          showIcons && /* @__PURE__ */ jsxRuntimeExports.jsx(RiShieldCrossLine, { size: 12 }),
          "Compliance risk"
        ] }) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { className: "whitespace-pre-wrap", children: props.conversation.compliance_risk_summary })
      ] }, "compliance-risk"),
      label: "Compliance risk"
    });
  }
  if (props.conversation.qa_risk_level === "low") {
    badges.push({
      node: /* @__PURE__ */ jsxRuntimeExports.jsx(QAReviewBadge, { showIcons }, "qa-review"),
      label: "QA review"
    });
  }
  if (badges.length === 0 && !sourceDeletedBadge) {
    return null;
  }
  const visibleBadges = badges.slice(0, VISIBLE_BADGE_COUNT);
  const overflowBadges = badges.slice(VISIBLE_BADGE_COUNT);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex min-w-0 max-w-full flex-wrap items-center gap-2", children: [
    sourceDeletedBadge,
    visibleBadges.map((b) => b.node),
    overflowBadges.length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs(ItemBadge, { className: "bg-neutral-200 text-neutral-700 rounded-md", children: [
        "+",
        overflowBadges.length
      ] }) }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: overflowBadges.map((b) => b.label).join(", ") })
    ] }, "overflow")
  ] });
}
function QAReviewBadge({ showIcons }) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(ItemBadge, { className: "bg-[#eff8e2] text-[#084825] rounded-md", children: [
    (showIcons ?? true) && /* @__PURE__ */ jsxRuntimeExports.jsx(Eye, { size: 12 }),
    "QA review"
  ] });
}
function DisputeBadge({ showIcons }) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(ItemBadge, { className: "bg-[#FFD6BE] text-[#482B08] rounded-md", children: [
    (showIcons ?? true) && /* @__PURE__ */ jsxRuntimeExports.jsx(Banknote, { size: 12 }),
    "Dispute"
  ] });
}
function ConversationBadgeItem(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(
    Badge,
    {
      className: cn(
        "bg-white !border-[0.5px] px-2 border-neutral-300 !font-normal h-[22px] rounded-full gap-2 text-xs-plus-plus whitespace-nowrap",
        props.className
      ),
      children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(RiPriceTag3Line, { className: "size-3" }),
        props.label
      ]
    }
  );
}
function getSentimentDisplay(summary) {
  switch (summary.level) {
    case "negative":
      return { text: "Negative sentiment", icon: RiEmotionSadLine };
    case "positive":
      return { text: "Positive sentiment", icon: RiEmotionHappyLine };
    default:
      return { text: "Neutral sentiment", icon: RiEmotionNormalLine };
  }
}
const badgeVariants = cva("rounded-md", {
  variants: {
    level: {
      positive: "bg-success-50 text-success-foreground",
      negative: "bg-danger-50 text-danger-400",
      neutral: "bg-neutral-200 text-neutral-foreground"
    }
  }
});
function SourceDeletedBadge(props) {
  if (!shouldShowSourceDeletedBadge(props.conversation)) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: /* @__PURE__ */ jsxRuntimeExports.jsx(ItemBadge, { className: "bg-neutral-100 text-neutral-500 rounded-md", children: sourceDeletedBadgeLabel(props.conversation) }) }) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: sourceDeletedTooltip(props.conversation) })
  ] });
}
function SentimentBadge(props) {
  const sentimentData = getSentimentDisplay(props.summary);
  const IconComponent = sentimentData.icon;
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(ItemBadge, { className: cn(badgeVariants({ level: props.summary.level })), children: [
    (props.showIcons ?? true) && /* @__PURE__ */ jsxRuntimeExports.jsx(IconComponent, { className: `size-3` }),
    sentimentData.text
  ] });
}
const ItemBadge = ({
  children,
  className
}) => {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "div",
    {
      className: cn(
        "items-center shrink-0 justify-center gap-2 px-1.5 py-0.5 rounded-md font-medium flex whitespace-nowrap min-w-fit",
        className
      ),
      children
    }
  );
};
function SLABreachBadge(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
      "button",
      {
        "aria-label": "SLA Breach",
        className: "bg-danger-50 text-danger-500 rounded-md px-1.5 py-0.5 flex items-center gap-2 hover:bg-red-100 transition-colors whitespace-nowrap",
        children: [
          (props.showIcons ?? true) && /* @__PURE__ */ jsxRuntimeExports.jsx(RiTimeFill, { className: "fill-danger-500", size: 12 }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { children: "SLA Breach" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      TooltipContent,
      {
        className: "max-w-[360px] py-2 px-3 shadow-xs rounded-lg",
        side: "bottom",
        sideOffset: 10,
        align: "start",
        children: /* @__PURE__ */ jsxRuntimeExports.jsx(SLABreachMessage, { slaBreaches: props.slaBreaches })
      }
    )
  ] });
}
function SLABreachMessage(props) {
  const breachGroups = groupBreachesByType(props.slaBreaches);
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "space-y-1", children: breachGroups.map((group, index) => /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "text-sm text-neutral-600 leading-[133%]", children: getBreachGroupMessage(group) }),
    group.breaches.map(
      (breach) => breach.workload_context ? /* @__PURE__ */ jsxRuntimeExports.jsx(
        SLAWorkloadDetails,
        {
          context: breach.workload_context
        },
        breach.id
      ) : null
    )
  ] }, index)) });
}
function SLAWorkloadDetails(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "border-t border-neutral-200 pt-2 text-xs text-neutral-600", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "font-medium text-neutral-800", children: [
      props.context.agent.name,
      " at assignment"
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(WorkloadSnapshot, { snapshot: props.context.at_assignment }),
    props.context.at_target_start && /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("p", { className: "mt-2 font-medium text-neutral-800", children: "At SLA start" }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(WorkloadSnapshot, { snapshot: props.context.at_target_start })
    ] })
  ] });
}
function WorkloadSnapshot(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("p", { className: "mt-0.5", children: [
    props.snapshot.assigned_email_count,
    " assigned emails ·",
    " ",
    props.snapshot.active_chat_count,
    " active chats ·",
    " ",
    props.snapshot.active_call_count === null ? "active calls unavailable" : `${props.snapshot.active_call_count} active calls`
  ] });
}
function groupBreachesByType(breaches) {
  const groups = /* @__PURE__ */ new Map();
  for (const breach of breaches) {
    const existing = groups.get(breach.type) || [];
    groups.set(breach.type, [...existing, breach]);
  }
  return Array.from(groups.entries()).map(([type, breachList]) => ({
    type,
    breaches: breachList
  }));
}
function formatThresholdList(breaches) {
  const thresholds = breaches.map((b) => formatThreshold(b.threshold));
  const uniqueThresholds = [...new Set(thresholds)];
  if (uniqueThresholds.length === 1) {
    return uniqueThresholds[0] ?? "";
  }
  if (uniqueThresholds.length === 2) {
    return `${uniqueThresholds[0]} and ${uniqueThresholds[1]}`;
  }
  const last = uniqueThresholds.pop();
  return `${uniqueThresholds.join(", ")}, and ${last}`;
}
function getBreachGroupMessage(group) {
  if (group.breaches.length === 0) {
    return "";
  }
  const thresholdText = formatThresholdList(group.breaches);
  const hasMultiple = group.breaches.length > 1;
  const timeframeWord = hasMultiple ? "timeframes" : "timeframe";
  switch (group.type) {
    case "email_response_time":
      return `The email was not responded to within the required SLA ${timeframeWord} (${thresholdText}).`;
    case "chat_response_time":
      return `The chat message was not responded to within the required SLA ${timeframeWord} (${thresholdText}).`;
    case "resolution_time":
      return `The ticket was not resolved within the required SLA ${timeframeWord} (${thresholdText}).`;
    default:
      return `SLA requirements were not met (${thresholdText}).`;
  }
}
function formatThreshold(threshold) {
  const secs = typeof threshold === "number" ? threshold : parseInt(threshold.toString(), 10) || 0;
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor(secs % 3600 / 60);
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  } else {
    return `${secs} second${secs > 1 ? "s" : ""}`;
  }
}

function IntegrationIcon(props) {
  const src = props.dataSource.id === "xcally" || props.dataSource.id === "alloy" || props.dataSource.id === "ringover" || props.dataSource.id === "gradient_labs" ? `/img/integrations/${props.dataSource.id}.png` : `/img/integrations/${props.dataSource.id}.svg`;
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "img",
    {
      src,
      alt: props.dataSource.name,
      className: cn("size-4", props.className)
    }
  );
}

function SourceDeletedExternalLinkHint(props) {
  const { conversation, className, showSourceName = true, showExternalIcon = true } = props;
  const sourceName = sourceDeletedDisplayName(conversation);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
      "button",
      {
        type: "button",
        className: cn(
          buttonVariants({ variant: "ghost" }),
          "px-0 text-neutral-500 min-w-fit font-medium cursor-default hover:bg-transparent",
          className
        ),
        children: [
          conversation.data_source && /* @__PURE__ */ jsxRuntimeExports.jsx(
            IntegrationIcon,
            {
              dataSource: conversation.data_source,
              className: "size-3"
            }
          ),
          showSourceName && sourceName,
          showExternalIcon && /* @__PURE__ */ jsxRuntimeExports.jsx(
            "img",
            {
              src: "/img/icons/ri-arrow-up-right-long.svg",
              alt: "External link",
              className: "size-3 text-neutral-500 min-w-fit opacity-50"
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: sourceDeletedTooltip(conversation) })
  ] });
}
function ConversationExternalLink(props) {
  const { conversation, className } = props;
  if (conversation.external_url == null) {
    return null;
  }
  const sourceName = conversationIsSourceDeleted(conversation) ? sourceDeletedDisplayName(conversation) : conversation.data_source?.name || "External";
  if (conversationIsSourceDeleted(conversation)) {
    return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex items-center gap-1 min-w-fit", children: /* @__PURE__ */ jsxRuntimeExports.jsx(
      SourceDeletedExternalLinkHint,
      {
        conversation,
        className
      }
    ) });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex items-center gap-1 min-w-fit", children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
    "a",
    {
      className: cn(
        buttonVariants({ variant: "ghost" }),
        "px-0 text-neutral-500 min-w-fit font-medium",
        className
      ),
      href: conversation.external_url,
      target: "_blank",
      rel: "noopener noreferrer",
      children: [
        conversation.data_source && /* @__PURE__ */ jsxRuntimeExports.jsx(
          IntegrationIcon,
          {
            dataSource: conversation.data_source,
            className: "size-3"
          }
        ),
        sourceName,
        " ",
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "img",
          {
            src: "/img/icons/ri-arrow-up-right-long.svg",
            alt: "External link",
            className: "size-3 text-neutral-500 min-w-fit"
          }
        )
      ]
    }
  ) });
}

function ConversationTimeDisplay(props) {
  const date = new Date(props.date);
  const displayTime = toLocaleString(date, props.variant ?? "long");
  const tooltipDate = date.toLocaleString(void 0, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    timeZoneName: "short"
  });
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsx(
      "time",
      {
        dateTime: props.date,
        className: props.className,
        children: displayTime
      },
      props.date
    ) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { className: "max-w-[275px]", children: props.tooltipLabel ? `${props.tooltipLabel} ${tooltipDate}` : tooltipDate })
  ] });
}
function toLocaleString(date, variant) {
  if (isToday(date)) {
    return date.toLocaleTimeString(void 0, {
      hour: "numeric",
      minute: "numeric",
      hour12: false
    });
  } else if (isThisYear(date)) {
    return variant === "long" ? date.toLocaleTimeString(void 0, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false
    }) : date.toLocaleDateString(void 0, {
      month: "short",
      day: "numeric"
    });
  } else {
    return variant === "long" ? date.toLocaleTimeString(void 0, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false
    }) : date.toLocaleDateString(void 0, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }
}

function CustomerImage(props) {
  const label = nonBlank(props.customer?.name)?.split(" ").map((word) => {
    if (word.match(/^\+[\d]/)) {
      const match = word.match(/^\+[\d]+/);
      return match ? match[0] : "";
    }
    return word.charAt(0).match(/[a-zA-Z]/) ? word.charAt(0).toUpperCase() : "";
  }).join("").slice(0, 2).trim() || "UC";
  return /* @__PURE__ */ jsxRuntimeExports.jsx(UserImage, { type: "customer", bgType: props.type, size: props.size, children: label });
}
function CustomerImageAndName(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex gap-2 items-center overflow-hidden", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(CustomerImage, { customer: props.client, size: "sm" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(CustomerName, { customer: props.client })
  ] });
}
function CustomerName(props) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "font-medium text-[13.5px] whitespace-nowrap w-full truncate", children: getCustomerName(props.customer) });
}
function getCustomerName(customer) {
  return nonBlank(customer?.name) ?? nonBlank(customer?.email) ?? "Unknown customer";
}
function nonBlank(value) {
  const trimmed = value?.trim();
  return trimmed !== void 0 && trimmed !== "" ? trimmed : void 0;
}

function CustomerProfile(props) {
  return props.customers && props.customers.length > 0 ? /* @__PURE__ */ jsxRuntimeExports.jsx(
    Customers,
    {
      customers: props.customers,
      type: props.type,
      size: props.size
    }
  ) : /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex gap-4 items-center", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "size-8 rounded-full text-xs bg-primary-100 flex shrink-0 items-center justify-center font-medium", children: "UC" }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "font-medium", children: "Unknown customer" })
  ] });
}
function Customers(props) {
  let primaryCustomer = props.customers[0];
  let otherCustomers = props.customers.slice(1);
  if (props.customers?.length > 1) {
    const nonBotCustomer = props.customers.find(
      (customer) => !customer.name?.includes(" Bot")
    );
    if (nonBotCustomer) {
      primaryCustomer = nonBotCustomer;
      otherCustomers = props.customers.filter(
        (customer) => customer.id !== nonBotCustomer.id
      );
    }
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex gap-2 items-center", children: [
    /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: cn("relative", otherCustomers.length > 0 && "mr-3.5"), children: [
      primaryCustomer != void 0 && /* @__PURE__ */ jsxRuntimeExports.jsx(
        CustomerImage,
        {
          customer: primaryCustomer,
          type: props.type,
          size: props.size || "md"
        }
      ),
      otherCustomers.length > 0 && /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipProvider, { children: /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "absolute bottom-0 flex items-center justify-center -right-[13.5px] h-[20px] p-0.5 rounded-sm border border-white text-xs bg-neutral-200", children: [
          "+",
          otherCustomers.length
        ] }) }),
        /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { className: "w-40", children: /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-col gap-2", children: otherCustomers.map((customer) => /* @__PURE__ */ jsxRuntimeExports.jsx("div", { children: customer.name || customer.email || customer.phone }, customer.id)) }) })
      ] }) })
    ] }),
    /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex flex-col", children: /* @__PURE__ */ jsxRuntimeExports.jsx(CustomerName, { customer: primaryCustomer }) })
  ] });
}

function LinkedActivityRowList({
  children,
  className
}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "div",
    {
      className: cn(
        "grid w-full grid-cols-[minmax(0,1fr)_auto_auto] gap-x-2 gap-y-1",
        className
      ),
      children
    }
  );
}
function LinkedActivityRow({
  href,
  render,
  ref,
  leading,
  title,
  identifier,
  badges,
  people,
  date,
  className,
  ...props
}) {
  return useRender({
    defaultTagName: href ? "a" : "div",
    render,
    ref,
    props: {
      ...props,
      ...href ? { href, target: "_blank", rel: "noopener noreferrer" } : {},
      className: cn(
        "group/activity relative col-span-full grid grid-cols-subgrid items-center gap-x-2 rounded-xl px-3 py-1.5 text-left text-[13.5px] transition-colors before:pointer-events-none before:absolute before:left-6 before:top-1/2 before:h-[calc(100%+0.25rem)] before:w-px before:-translate-x-1/2 before:bg-neutral-300 before:content-[''] last:before:hidden",
        (href || render) && "cursor-pointer hover:bg-neutral-100",
        className
      ),
      children: /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
        /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
          leading && /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full bg-white group-hover/activity:bg-neutral-100", children: leading }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "truncate", children: title }),
          identifier && /* @__PURE__ */ jsxRuntimeExports.jsx("span", { className: "shrink-0 text-neutral-500", children: identifier }),
          badges
        ] }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex items-center justify-end", children: people }),
        /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex items-center justify-end", children: date && /* @__PURE__ */ jsxRuntimeExports.jsx(
          ConversationTimeDisplay,
          {
            date,
            variant: "short",
            className: "shrink-0 text-secondary-foreground"
          }
        ) })
      ] })
    }
  });
}
function LinkedActivityRowIcon({ children }) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { className: "flex size-6 shrink-0 items-center justify-center text-neutral-600", children });
}

function getNextUpdateInterval(date) {
  const now = /* @__PURE__ */ new Date();
  const minutesDiff = differenceInMinutes(now, date);
  const hoursDiff = differenceInHours(now, date);
  if (minutesDiff < 60) {
    return 1e3 * 60;
  } else if (hoursDiff < 24) {
    return 1e3 * 60 * 60;
  } else {
    return 1e3 * 60 * 60 * 24;
  }
}
function useRelativeTimeDifference(dateStr) {
  const [relativeTimeDifference, setRelativeTimeDifference] = useState(
    () => getRelativeTimeDifference(dateStr)
  );
  useEffect(() => {
    const updateTimeDifference = () => {
      setRelativeTimeDifference(getRelativeTimeDifference(dateStr));
    };
    const dateDate = new Date(dateStr);
    const interval = getNextUpdateInterval(dateDate);
    updateTimeDifference();
    const timer = setInterval(updateTimeDifference, interval);
    return () => clearInterval(timer);
  }, [dateStr]);
  return relativeTimeDifference;
}
function RelativeTimeDifference(props) {
  const relativeTimeDifference = useRelativeTimeDifference(props.date);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { className: props.className, children: /* @__PURE__ */ jsxRuntimeExports.jsxs(
      "time",
      {
        dateTime: props.date,
        className: props.className,
        children: [
          relativeTimeDifference,
          " ",
          props.suffix
        ]
      },
      props.date
    ) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { children: new Date(props.date).toLocaleString() })
  ] });
}
function getRelativeTimeDifference(dateStr) {
  const dateDate = new Date(dateStr);
  const now = /* @__PURE__ */ new Date();
  const secondsDiff = differenceInSeconds(now, dateDate);
  const minutesDiff = differenceInMinutes(now, dateDate);
  const hoursDiff = differenceInHours(now, dateDate);
  const daysDiff = differenceInDays(now, dateDate);
  const weeksDiff = Math.floor(daysDiff / 7);
  const monthsDiff = Math.floor(daysDiff / 30);
  const yearsDiff = Math.floor(daysDiff / 365);
  if (yearsDiff > 0) {
    return `${yearsDiff}y`;
  }
  if (monthsDiff > 0) {
    return `${monthsDiff}mo`;
  }
  if (weeksDiff > 0) {
    return `${weeksDiff}w`;
  }
  if (daysDiff > 0) {
    return `${daysDiff}d`;
  }
  if (hoursDiff > 0) {
    return `${hoursDiff}h`;
  }
  if (minutesDiff > 0) {
    return `${minutesDiff}m`;
  }
  return `${secondsDiff}s`;
}

function SlackMessageView(props) {
  if (props.item.type !== "slack_message" || !props.item.slack_message) {
    return null;
  }
  const slackMessage = props.item.slack_message;
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    LinkedActivityRow,
    {
      href: slackMessage.url,
      leading: /* @__PURE__ */ jsxRuntimeExports.jsx(LinkedActivityRowIcon, { children: /* @__PURE__ */ jsxRuntimeExports.jsx(SlackIcon, {}) }),
      title: "Slack thread",
      identifier: props.item.slack_message_origin === "notification" ? "Notification" : void 0,
      date: props.item.entity_created_at || props.item.created_at
    }
  );
}
function SlackIcon() {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 24 24",
      fill: "none",
      "aria-hidden": "true",
      className: "size-4 shrink-0",
      children: [
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "path",
          {
            d: "M6.36125 14.5772C6.36125 15.7106 5.44597 16.6266 4.3135 16.6266C3.18104 16.6266 2.26576 15.7106 2.26576 14.5772C2.26576 13.4439 3.18104 12.5279 4.3135 12.5279H6.36125V14.5772ZM7.38512 14.5772C7.38512 13.4439 8.3004 12.5279 9.43286 12.5279C10.5653 12.5279 11.4806 13.4439 11.4806 14.5772V19.7006C11.4806 20.834 10.5653 21.75 9.43286 21.75C8.3004 21.75 7.38512 20.834 7.38512 19.7006V14.5772Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "path",
          {
            d: "M9.43262 6.34872C8.30016 6.34872 7.38488 5.43272 7.38488 4.29936C7.38488 3.166 8.30016 2.25 9.43262 2.25C10.5651 2.25 11.4804 3.166 11.4804 4.29936V6.34872H9.43262ZM9.43262 7.38893C10.5651 7.38893 11.4804 8.30493 11.4804 9.43829C11.4804 10.5717 10.5651 11.4877 9.43262 11.4877H4.29775C3.16528 11.4877 2.25 10.5717 2.25 9.43829C2.25 8.30493 3.16528 7.38893 4.29775 7.38893H9.43262Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "path",
          {
            d: "M17.639 9.43829C17.639 8.30493 18.5543 7.38893 19.6867 7.38893C20.8192 7.38893 21.7345 8.30493 21.7345 9.43829C21.7345 10.5717 20.8192 11.4877 19.6867 11.4877H17.639V9.43829ZM16.6151 9.43829C16.6151 10.5717 15.6998 11.4877 14.5674 11.4877C13.4349 11.4877 12.5196 10.5717 12.5196 9.43829V4.29936C12.5196 3.166 13.4349 2.25 14.5674 2.25C15.6998 2.25 16.6151 3.166 16.6151 4.29936V9.43829Z",
            fill: "currentColor"
          }
        ),
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          "path",
          {
            d: "M14.5674 17.6513C15.6998 17.6513 16.6151 18.5673 16.6151 19.7006C16.6151 20.834 15.6998 21.75 14.5674 21.75C13.4349 21.75 12.5196 20.834 12.5196 19.7006V17.6513H14.5674ZM14.5674 16.6266C13.4349 16.6266 12.5196 15.7106 12.5196 14.5772C12.5196 13.4439 13.4349 12.5279 14.5674 12.5279H19.7023C20.8347 12.5279 21.75 13.4439 21.75 14.5772C21.75 15.7106 20.8347 16.6266 19.7023 16.6266H14.5674Z",
            fill: "currentColor"
          }
        )
      ]
    }
  );
}

function CopyTicketNumberButton({
  externalId,
  className
}) {
  const [copied, setCopied] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const buttonRef = useRef(null);
  const timeoutRef = useRef(null);
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(externalId);
    setCopied(true);
    setTooltipOpen(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
      setTooltipOpen(false);
    }, 2e3);
  };
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCopied(false);
    setTooltipOpen(false);
  };
  const handleTooltipOpenChange = (open) => {
    if (open || !copied) {
      setTooltipOpen(open);
    }
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(Tooltip, { open: tooltipOpen, onOpenChange: handleTooltipOpenChange, children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsxRuntimeExports.jsx(
      "button",
      {
        ref: buttonRef,
        className: cn(`flex items-center gap-1 cursor-pointer`, className),
        onClick: handleCopy,
        onMouseLeave: handleMouseLeave,
        children: externalId
      }
    ) }),
    /* @__PURE__ */ jsxRuntimeExports.jsx(TooltipContent, { side: "bottom", children: copied ? "Copied" : "Copy" })
  ] });
}

export { AgentImage, AgentImageAndName, AgentName, Agents, Avatar, AvatarFallback, AvatarImage, Badge, ConversationBadgeItem, ConversationBadges, ConversationExternalLink, ConversationTimeDisplay, CopyTicketNumberButton, CustomerImage, CustomerImageAndName, CustomerName, CustomerProfile, DisputeAcknowledgementDueDateBadge, DisputeDueDateBadge, DisputeResolutionDueDateBadge, IntegrationIcon, LinkedActivityRow, LinkedActivityRowIcon, LinkedActivityRowList, PromptInputDisplayAttachmentChip, RelativeTimeDifference, SlackMessageView, SourceDeletedBadge, SourceDeletedExternalLinkHint, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, UserImage, UserPersona, buttonVariants, conversationIsSourceDeleted, getAgentName, getCustomerName, promptInputAttachmentIcon, shouldShowSourceDeletedBadge, sourceDeletedBadgeLabel, sourceDeletedDisplayName, sourceDeletedTooltip, toLocaleString };
